import * as XLSX from "xlsx";

export const MAX_IMPORT_ROWS = 5000;

export const LEAD_IMPORT_FIELDS = [
  { key: "name", label: "Lead name", required: true, group: "Contact" },
  { key: "email", label: "Email", group: "Contact" },
  { key: "phone", label: "Phone", group: "Contact" },
  { key: "company", label: "Company", group: "Company" },
  { key: "website", label: "Website", group: "Company" },
  { key: "designation", label: "Designation", group: "Company" },
  { key: "company_location", label: "Company location", group: "Company" },
  { key: "team_size", label: "Team size", group: "Company" },
  { key: "revenue", label: "Revenue", group: "Company" },
  { key: "source", label: "Lead source", group: "Lead context" },
  { key: "priority_area", label: "Priority area", group: "Lead context" },
  { key: "priority_area_type", label: "Priority type", group: "Lead context" },
];

const ALIASES = {
  name: ["name", "full name", "fullname", "lead name", "contact name", "customer name"],
  email: ["email", "e mail", "email address", "work email"],
  phone: ["phone", "phone number", "mobile", "mobile number", "contact number", "whatsapp"],
  company: ["company", "company name", "business", "organisation", "organization"],
  website: ["website", "website url", "company website", "domain"],
  designation: ["designation", "job title", "title", "position"],
  company_location: ["company location", "location", "city", "address"],
  team_size: ["team size", "company size", "employees", "employee count"],
  revenue: ["revenue", "annual revenue", "budget", "deal value"],
  source: ["source", "lead source", "channel", "utm source"],
  priority_area: ["priority area", "interest", "requirement", "service"],
  priority_area_type: ["priority area type", "interest type", "requirement type", "service type"],
};

const normalizeHeader = (value) => String(value || "")
  .trim()
  .toLowerCase()
  .replace(/[_-]+/g, " ")
  .replace(/[^a-z0-9 ]+/g, "")
  .replace(/\s+/g, " ");

function dedupeHeaders(values) {
  const seen = new Set();

  return values.map((value, index) => {
    const base = String(value || "").trim() || `column_${index + 1}`;
    let name = base;
    let suffix = 2;

    while (seen.has(name)) name = `${base} (${suffix++})`;
    seen.add(name);
    return name;
  });
}

function worksheetToRows(worksheet) {
  const matrix = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: "",
    raw: false,
    blankrows: false,
  });

  if (!matrix.length) throw new Error("This source is empty.");

  const columns = dedupeHeaders(matrix[0]);
  const allRows = matrix.slice(1)
    .map((values) => {
      const row = {};
      columns.forEach((column, index) => {
        const value = values[index];
        row[column] = value == null ? "" : String(value).trim();
      });
      return row;
    })
    .filter((row) => Object.values(row).some(Boolean));

  if (!columns.length) throw new Error("Add a header row with your column names.");
  if (!allRows.length) throw new Error("Add at least one lead below the header row.");

  return {
    columns,
    rows: allRows.slice(0, MAX_IMPORT_ROWS),
    totalRows: allRows.length,
    truncated: allRows.length > MAX_IMPORT_ROWS,
  };
}

function firstWorksheet(workbook) {
  const name = workbook.SheetNames?.[0];
  if (!name || !workbook.Sheets?.[name]) throw new Error("No worksheet was found.");
  return workbook.Sheets[name];
}

export async function parseLeadFile(file) {
  if (!(file instanceof File)) throw new Error("Choose a spreadsheet file first.");
  if (file.size > 10 * 1024 * 1024) throw new Error("Choose a file smaller than 10 MB.");
  const workbook = XLSX.read(await file.arrayBuffer(), { type: "array" });
  return worksheetToRows(firstWorksheet(workbook));
}

export function parsePastedRows(value) {
  const text = String(value || "").trim();
  if (!text) throw new Error("Paste a header row and at least one lead.");
  const workbook = XLSX.read(text, { type: "string" });
  return worksheetToRows(firstWorksheet(workbook));
}

export function suggestLeadMapping(columns = [], customFields = []) {
  const mapping = {};
  const used = new Set();

  Object.entries(ALIASES).forEach(([field, aliases]) => {
    const match = columns.find((column) => {
      if (used.has(column)) return false;
      return aliases.includes(normalizeHeader(column));
    });

    if (match) {
      mapping[field] = match;
      used.add(match);
    }
  });

  customFields.forEach((field) => {
    const aliases = [field.key, field.label].map(normalizeHeader);
    const match = columns.find((column) => !used.has(column) && aliases.includes(normalizeHeader(column)));
    if (match) {
      mapping[`custom:${field.key}`] = match;
      used.add(match);
    }
  });

  return mapping;
}
